import jsPDF from "jspdf";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { TResult } from "../../hooks/useDocumentListWithData";
import { getTitle } from "../../utils/getTitle";
import { screeningStatusModel, isOngoingMonitoring } from "../DatabaseScreeningPanel";
import logo from "./logo.png";
import { TOnboardingConfig } from "../../../app/models";
import { ptSans } from "./encodeSans";
import { TDocumentData, TUserFullInfo, TVerificationRequest } from "../../models";
import { AUTH_COMPANY_PURPOSE } from "../../../company/models";
import { ICompanyVerificationStatusesConfig } from "../../network";
import { buildQuestionnairePdfPlan } from "./pdf/buildQuestionnairePdfPlan";
import { PdfSection } from "./pdf/types";
import {
  FileType,
  getFileTypeFromMimeType,
} from "../../utils/fileTypeDetection";

const PAGE_MARGIN = 20;
const FULLPAGE_WIDTH = 595;
const PAGE_WIDTH = FULLPAGE_WIDTH - PAGE_MARGIN * 2;
const PAGE_HEIGHT = 842 - PAGE_MARGIN * 2;

const MAX_IMAGE_HEIGHT = PAGE_HEIGHT / 4;
const MAX_IMAGE_WIDTH = PAGE_WIDTH / 2;

const FILE_TYPE_LINE_HEIGHT = 14;

const SCREENING_FOOTNOTE_TEXT = "* The user profiles are screened against global databases comprising"
  + " politically exposed persons, sanctions lists, watchlists and adverse media."
  + " This includes all major international and national lists published"
  + " by governments and independent, international and non-governmental"
  + " organizations (OFAC, UN, HMT, EU, DFAT and many more lists from"
  + " around the world).";

const BUSINESS_PROFILE_NOTES = [
  "Notes: Contracting party and beneficial owner is the client, acting personally with no representative.",
  "The client may use hosted and unhosted wallets, interacting with DEFI and crypto exchanges.",
  "Planned transactions: bank and crypto deposits, withdrawals, and operations with VNX tokens.",
  "The volume of incoming payments is expected to be equal to or slightly lower than the volume of outgoing payments.",
  "The CV or Occupation verification, if enclosed in the due diligence files, forms an integral part of this Business Profile.",
  "Whitelisted wallets (TT identifiers) : Not recorded.",
];

let reservedBottomSpace = 0;

interface IAppendix {
  link: string;
  content: Blob;
  title: string;
}

export type ApplicantSummaryFileContent = {
  contentType: string | null;
  fileType?: FileType;
  response?: Response;
};

export const resolveApplicantSummaryFileContent = async (
  file: TDocumentData,
): Promise<ApplicantSummaryFileContent> => {
  const metadataContentType = file.fileInfo?.mimeType?.trim();
  if (metadataContentType) {
    const fileType = getFileTypeFromMimeType(metadataContentType);
    if (fileType === FileType.pdf) {
      return {
        contentType: metadataContentType,
        fileType,
        response: await fetch(file.dataLink),
      };
    }

    return {
      contentType: metadataContentType,
      fileType,
    };
  }

  const response = await fetch(file.dataLink);
  const contentType = response.headers.get("content-type");
  return {
    contentType,
    fileType: getFileTypeFromMimeType(contentType),
    response,
  };
};

const addPage = (pdf: jsPDF, margin: number = PAGE_MARGIN * 2) => {
  reservedBottomSpace = 0;
  pdf.addPage();
  addLogoToTop(pdf);
  let startY = margin;
  startY += 30;
  return startY;
};

const getPageLimit = () => PAGE_HEIGHT - reservedBottomSpace;

type PdfFontStyle = "italic" | "normal" | "bold";

interface PdfTextStyleOptions {
  fontSize?: number;
  color?: string;
  leftMargin?: number;
  lineHeight?: number;
  fontStyle?: PdfFontStyle;
}

interface PdfTextBlockOptions extends PdfTextStyleOptions {
  marginTop?: number;
}

const applyPdfTextStyle = (
  pdf: jsPDF,
  {
    fontSize = 10,
    color = "#000000",
    fontStyle = "normal",
  }: Pick<PdfTextStyleOptions, "fontSize" | "color" | "fontStyle"> = {}
) => {
  pdf.setFontSize(fontSize);
  pdf.setTextColor(color);
  pdf.addFileToVFS("ptsans.ttf", ptSans[fontStyle]);
  pdf.addFont("ptsans.ttf", "ptsans", fontStyle);
  pdf.setFont("ptsans", fontStyle);
};

const getWrappedText = (
  pdf: jsPDF,
  text: string,
  {
    fontSize = 10,
    color = "#000000",
    leftMargin = PAGE_MARGIN * 2,
    fontStyle = "normal",
  }: Pick<PdfTextStyleOptions, "fontSize" | "color" | "leftMargin" | "fontStyle"> = {}
) => {
  applyPdfTextStyle(pdf, { fontSize, color, fontStyle });
  return pdf.splitTextToSize(
    text,
    FULLPAGE_WIDTH - leftMargin - PAGE_MARGIN * 2
  ) as string[];
};

const getTextBlockHeight = (
  pdf: jsPDF,
  text: string,
  option?: PdfTextBlockOptions
) => {
  const fontSize = option?.fontSize ?? 10;
  const leftMargin = option?.leftMargin ?? PAGE_MARGIN * 2;
  const lineHeight = option?.lineHeight ?? fontSize * 1.66;
  const marginTop = option?.marginTop ?? 0;
  const lines = getWrappedText(pdf, text, {
    fontSize,
    color: option?.color,
    leftMargin,
    fontStyle: option?.fontStyle,
  });

  return {
    lines,
    height: marginTop + lines.length * lineHeight,
  };
};

const writeTextBlock = (
  pdf: jsPDF,
  text: string,
  startY: number,
  option?: PdfTextBlockOptions
) => {
  const fontSize = option?.fontSize ?? 10;
  const leftMargin = option?.leftMargin ?? PAGE_MARGIN * 2;
  const lineHeight = option?.lineHeight ?? fontSize * 1.66;
  const marginTop = option?.marginTop ?? 0;
  const { lines } = getTextBlockHeight(pdf, text, option);

  let currentY = startY + marginTop;
  lines.forEach((line) => {
    currentY += lineHeight;
    pdf.text(line, leftMargin, currentY);
  });

  return currentY;
};

const writeFileType = (
  pdf: jsPDF,
  startY: number,
  type: string | null,
  leftMargin: number
) => {
  startY = writelineInPdf(pdf, `type: ${type}`, startY, {
    fontSize: 8,
    lineHeight: FILE_TYPE_LINE_HEIGHT,
    leftMargin: leftMargin,
  });
  return startY;
};

const addImageFile = (
  pdf: jsPDF,
  link: string,
  startY: number,
  contentType: string | null,
  leftMargin: number,
  topMargin: number
) => {
  return new Promise<{ imageToShow: string; width: number; height: number }>((resolve) => {
    const image = new Image();
    let imageToShow = null;
    let width, height: number;
    image.setAttribute("crossOrigin", "anonymous");
    image.src = link;
    image.onload = function () {
      var canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      [width, height] = [image.naturalWidth, image.naturalHeight];
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#e5e5e5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      imageToShow = canvas.toDataURL("image/jpeg");
      resolve({ imageToShow, width, height });
    };
  }).then((image: any) => {
    let imageHeight = image.height;
    let imageWidth = image.width;
    if (imageHeight > MAX_IMAGE_HEIGHT) {
      let k = MAX_IMAGE_WIDTH / imageHeight;
      imageHeight *= k;
      imageWidth *= k;
    }
    if (imageWidth > MAX_IMAGE_WIDTH) {
      let k = MAX_IMAGE_WIDTH / imageWidth;
      imageHeight *= k;
      imageWidth *= k;
    }

    const yAfterImage = startY + imageHeight + topMargin;
    const pageLimit = getPageLimit();
    if (yAfterImage + FILE_TYPE_LINE_HEIGHT >= pageLimit) {
      startY = addPage(pdf, PAGE_MARGIN * 2 + imageHeight);
    } else {
      startY = yAfterImage;
    }
    pdf.addImage(
      image.imageToShow,
      "PNG",
      leftMargin,
      startY - imageHeight,
      imageWidth,
      imageHeight
    );

    startY = writeFileType(pdf, startY, contentType, leftMargin);
    return startY;
  });
};

const addFile = async (
  pdf: jsPDF,
  file: TDocumentData,
  startY: number,
  appendix: IAppendix[],
  option: { leftMargin?: number; topMargin?: number }
) => {
  const leftMargin = option?.leftMargin ?? PAGE_MARGIN * 2;
  const topMargin = option?.topMargin ?? 10;

  startY = writelineInPdf(pdf, `· ${file.dataId}`, startY, {
    leftMargin: PAGE_MARGIN * 3,
  });

  if (!file.dataLink) return startY;

  const { contentType, fileType, response } =
    await resolveApplicantSummaryFileContent(file);

  if (fileType === FileType.image) {
    return await addImageFile(
      pdf, file.dataLink, startY, contentType, leftMargin, topMargin
    );
  } else if (fileType === FileType.pdf) {
    startY = writeFileType(pdf, startY, contentType, leftMargin);
    const pdfResponse = response ?? await fetch(file.dataLink);
    const blob = await pdfResponse.blob();
    appendix.push({
      link: file.dataLink,
      content: blob,
      title: file.dataId,
    });
    startY = writelineInPdf(pdf, `See Appendix ${appendix.length}`, startY, {
      leftMargin: leftMargin,
    });
    return startY;
  } else {
    startY = writeFileType(pdf, startY, contentType, leftMargin);
    startY += 12;
    pdf.setTextColor("blue");
    pdf.textWithLink("Please download it here", leftMargin, startY, {
      url: file.dataLink,
    });
    pdf.setTextColor("#000000");
    return startY;
  }
};

const writelineInPdf = (
  pdf: jsPDF,
  initialText: string,
  startY: number,
  option?: {
    fontSize?: number;
    color?: string;
    topMargin?: number;
    leftMargin?: number;
    lineHeight?: number;
    fontStyle?: "italic" | "normal" | "bold";
    isLineBreak?: boolean;
  }
) => {
  const fontSize = option?.fontSize ?? 10;
  const color = option?.color ?? "#000000";
  const topMargin = option?.topMargin ?? 0;
  const leftMargin = option?.leftMargin ?? PAGE_MARGIN * 2;
  const lineHeight = option?.lineHeight ?? fontSize * 1.66;
  const fontStyle = option?.fontStyle ?? "normal";
  const isLineBreak = option?.isLineBreak ?? true;

  let text = getWrappedText(pdf, initialText, {
    fontSize,
    color,
    leftMargin,
    fontStyle,
  });

  if (text[0]?.includes("Currency:")) {
    return startY;
  }
  for (let i = 0; i < text.length; i++) {
    if (isLineBreak) startY += topMargin + lineHeight;

    if (startY >= getPageLimit()) {
      startY = addPage(pdf);
    }
    pdf.text(text[i], leftMargin, startY);
  }

  return startY;
};

const addLogoToTop = (pdf: jsPDF) => {
  pdf.setFillColor("#ebeaea");
  var img = new Image();
  img.src = logo;
  pdf.rect(0, 0, 595, 50, "F");
  pdf.addImage(img, "png", 282, 10, 60, 30);
  pdf.setFillColor("#ffffff");
  pdf.rect(20, 50, 595, 20, "F");
  pdf.circle(20, 70, 20, "F");
  pdf.circle(595 - 20, 70, 20, "F");
};

interface IFootNote {
  pdf: jsPDF;
  withLine?: boolean;
  blocks: Array<
    {
      text: string;
    } & PdfTextBlockOptions
  >;
  currentY?: number;
}

const addFootnote = ({
  pdf,
  withLine = true,
  blocks,
  currentY,
}: IFootNote) => {
  const footerHeight = blocks.reduce((height, block) => {
    return height + getTextBlockHeight(pdf, block.text, block).height;
  }, 0);
  const footerY = PAGE_HEIGHT - footerHeight;
  let nextStartY = currentY ?? PAGE_MARGIN * 2;

  if (currentY !== undefined && currentY >= footerY) {
    nextStartY = addPage(pdf);
  }

  reservedBottomSpace = footerHeight;
  let y = footerY;
  if (withLine) {
    pdf.line(PAGE_MARGIN * 2, y, PAGE_WIDTH, y);
  }

  blocks.forEach((block) => {
    y = writeTextBlock(pdf, block.text, y, block);
  });

  return nextStartY;
};

async function writePdfSection(
  pdf: jsPDF,
  section: PdfSection,
  startY: number,
  appendix: IAppendix[]
): Promise<number> {
  startY = writelineInPdf(pdf, `${section.title}:`, startY, {
    fontSize: 12,
    lineHeight: 16,
  });
  for (const row of section.rows) {
    const text =
      row.value === ""
        ? row.label
        : `${row.label}:  ${row.value}`;
    startY = writelineInPdf(pdf, text, startY, {
      leftMargin: PAGE_MARGIN * 3,
      ...(row.value === "" && { fontSize: 10, fontStyle: "italic" }),
    });
  }
  if (section.attachments.length > 0) {
    startY = writelineInPdf(pdf, "Files:", startY, {
      fontSize: 10,
      leftMargin: PAGE_MARGIN * 2.5,
      fontStyle: "italic",
    });
  }
  for (const att of section.attachments) {
    startY = await addFile(pdf, att.file, startY, appendix, {
      leftMargin: PAGE_MARGIN * 3 + 8,
    });
  }
  return startY;
}

export interface IGeneratePdfParams {
  documents: TResult;
  userInfo: TUserFullInfo;
  comments: any;
  data: TVerificationRequest;
  id: string;
  statusesConfig: ICompanyVerificationStatusesConfig;
  countriesRegex: any;
  onboardingConfig: TOnboardingConfig | null;
}

export const generatePdf = async ({
  documents,
  userInfo,
  comments,
  data,
  id,
  statusesConfig,
  countriesRegex,
  onboardingConfig,
}: IGeneratePdfParams): Promise<void> => {
  const pdf = new jsPDF("p", "pt", "a4");
  let startY = PAGE_MARGIN;

  addLogoToTop(pdf);
  startY += 30;

  startY = writelineInPdf(pdf, "BUSINESS PROFILE", startY, {
    fontSize: 16,
    leftMargin: 230,
    fontStyle: "bold",
  });

  startY = writelineInPdf(pdf, getTitle(id!, data), startY, {
    fontSize: 16,
  });
  let date = new Date().toLocaleDateString();
  startY = writelineInPdf(pdf, `Report for ${date}`, startY, {
    fontStyle: "italic",
  });
  startY = writelineInPdf(pdf, `Verification ID ${id}`, startY, {
    leftMargin: 155,
    fontStyle: "italic",
    isLineBreak: false,
  });

  startY = writelineInPdf(pdf, "Verification Status:", startY, {});
  startY = writelineInPdf(
    pdf,
    statusesConfig?.object[data?.status]?.displayName,
    startY,
    {
      color: statusesConfig?.object[data?.status]?.color,
      isLineBreak: false,
      leftMargin: 155,
    }
  );
  startY = writelineInPdf(pdf, "Status Reason:", startY, {});
  startY = writelineInPdf(pdf, data.statusReason, startY, {
    isLineBreak: false,
    leftMargin: 155,
  });

  startY = writelineInPdf(pdf, "Risk Level:", startY, {});
  startY = writelineInPdf(pdf, data.riskLevel, startY, {
    isLineBreak: false,
    leftMargin: 155,
  });

  startY = writelineInPdf(pdf, "Risk Level Reason:", startY, {});
  startY = writelineInPdf(pdf, data.riskLevelReason, startY, {
    isLineBreak: false,
    leftMargin: 155,
  });

  startY = writelineInPdf(pdf, "Database screening *", startY, {
    fontSize: 16,
    lineHeight: 24,
  });
  startY = writelineInPdf(pdf, "Current status:", startY, {
    leftMargin: PAGE_MARGIN * 3,
  });
  const screeningStatus =
    screeningStatusModel[
      data.screeningStatus as keyof typeof screeningStatusModel
    ];
  startY = writelineInPdf(pdf, data.screeningStatus, startY, {
    leftMargin: 155,
    isLineBreak: false,
    color: screeningStatus?.color,
  });
  if (screeningStatus?.explanation) {
    startY = writelineInPdf(pdf, screeningStatus.explanation, startY, {
      leftMargin: PAGE_MARGIN * 3,
    });
  }
  startY = writelineInPdf(
    pdf,
    `Ongoing monitoring (daily screening): ${isOngoingMonitoring(data.screeningStatus) ? "ON" : "-"
    }`,
    startY,
    {
      leftMargin: PAGE_MARGIN * 3,
    }
  );
  if (data.declineReason?.length > 0) {
    startY = writelineInPdf(pdf, `Decline reasons:`, startY, {
      leftMargin: PAGE_MARGIN * 3,
    });
    data.declineReason.forEach((reason: string, index: number) => {
      startY = writelineInPdf(pdf, `${reason.replace(/_/g, " ")}`, startY, {
        leftMargin: 155,
        isLineBreak: index !== 0,
      });
    });
  }
  pdf.line(PAGE_MARGIN * 2, startY + 15, PAGE_WIDTH, startY + 15);

  startY = addFootnote({
    pdf,
    blocks: [
      {
        text: SCREENING_FOOTNOTE_TEXT,
        fontSize: 8,
        lineHeight: 10,
        marginTop: 8,
      },
    ],
    currentY: startY,
  });

  startY = writelineInPdf(pdf, "Questionnaire", startY, {
    fontSize: 16,
    topMargin: 15,
  });

  let appendix: IAppendix[] = [];
  const companyPurpose =
    typeof localStorage !== "undefined"
      ? localStorage.getItem(AUTH_COMPANY_PURPOSE) ?? ""
      : "";
  const sections = buildQuestionnairePdfPlan({
    onboardingConfig,
    documents,
    userInfo,
    comments,
    countriesRegex,
    companyPurpose,
  });
  for (const section of sections) {
    startY = await writePdfSection(pdf, section, startY, appendix);
  }

  addFootnote({
    pdf,
    withLine: false,
    blocks: [
      ...BUSINESS_PROFILE_NOTES.map((text) => ({
        text,
        fontSize: 10,
        lineHeight: 14,
      })),
      {
        text: "Signed by Compliance Officer: ________________________",
        fontSize: 12,
        lineHeight: 16,
        marginTop: 22,
      },
    ],
    currentY: startY,
  });
  reservedBottomSpace = 0;

  if (appendix.length > 0) {
    const pdfBytes = pdf.output("arraybuffer");
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    for (let i = 0; i < appendix.length; i++) {
      const separatorPage = pdfDoc.addPage([595, 842]);
      const appendixNumber = `Appendix ${i + 1}`;
      const numberSize = 24;
      const numberWidth = font.widthOfTextAtSize(appendixNumber, numberSize);
      
      separatorPage.drawText(appendixNumber, {
        x: (595 - numberWidth) / 2,
        y: 842 / 2 + 20,
        size: numberSize,
        font: font,
        color: rgb(0, 0, 0),
      });

      const appendixItem = appendix[i];
      const titleText = appendixItem.title;
      const titleSize = 14;
      const titleWidth = font.widthOfTextAtSize(titleText, titleSize);
      
      separatorPage.drawText(titleText, {
        x: (595 - titleWidth) / 2,
        y: 842 / 2 - 10,
        size: titleSize,
        font: font,
        color: rgb(0, 0, 0),
      });

      const appendixBytes = await appendixItem.content.arrayBuffer();
      const appendixDoc = await PDFDocument.load(appendixBytes);
      const copiedPages = await pdfDoc.copyPages(appendixDoc, appendixDoc.getPageIndices());
      
      copiedPages.forEach((page) => {
        pdfDoc.addPage(page);
      });
    }

    const finalPdfBytes = await pdfDoc.save();
    const blob = new Blob([new Uint8Array(finalPdfBytes)], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Business-Profile-${id}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } else {
    pdf.save(`Business-Profile-${id}.pdf`);
  }
};
